import { HoldReport } from "../components/HoldReport";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, Play } from "lucide-react";
import { useApi, useFetch, errMsg, inr, lastMonth } from "../lib/api";
import { PageHeader, Empty, Pill, VendorMonthBar, useVendors } from "../components/shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";

const Result = ({ r }) => (
  <div className={`c-surface p-6 mb-6 c-rise border-l-4 ${r.hold_payment ? "!border-l-rose-500" : "!border-l-emerald-500"}`} data-testid="hold-check-result">
    <div className="flex items-start gap-4">
      {r.hold_payment ? <ShieldAlert className="text-rose-400 shrink-0" size={28} /> : <ShieldCheck className="text-emerald-400 shrink-0" size={28} />}
      <div className="flex-1">
        <div className="font-mono text-xs text-slate-400">{r.reason_code}</div>
        <div className={`font-display text-2xl font-extrabold ${r.hold_payment ? "text-rose-400" : "text-emerald-400"}`} data-testid="hold-check-action">
          {r.hold_payment ? "BLOCK DISBURSEMENT" : "RELEASE PAYMENT"}</div>
        <div className="text-sm text-slate-300 mt-1">{r.reason}</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mt-5 font-mono text-sm">
          {[["Headcount", r.summary.headcount], ["Calc. EPF", inr(r.summary.calculated_pf)], ["Challan EPF", inr(r.summary.challan_pf)],
            ["Calc. ESIC", inr(r.summary.calculated_esic)], ["Challan ESIC", inr(r.summary.challan_esic)]].map(([k, v]) => (
            <div key={k}><div className="text-[10px] uppercase tracking-widest text-slate-500">{k}</div><div>{v}</div></div>))}
        </div>
      </div>
    </div>
  </div>
);

export default function Compliance() {
  const api = useApi();
  const { data: vendors } = useVendors();
  const { data: holds, reload } = useFetch("/compliance/holds");
  const [vendorId, setVendorId] = useState("");
  const [month, setMonth] = useState(lastMonth());
  const [result, setResult] = useState(null);
  const [release, setRelease] = useState(null);
  const [note, setNote] = useState("");
  useEffect(() => { if (!vendorId && vendors?.length) setVendorId(vendors[0].id); }, [vendors, vendorId]);

  const run = async () => {
    try { setResult((await api.post("/compliance/verify-hold", { vendor_id: vendorId, billing_month: month })).data); reload(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const doRelease = async (e) => {
    e.preventDefault();
    try { await api.post(`/compliance/holds/${release.id}/release`, { note }); toast.success("Payment released with override"); setRelease(null); setResult(null); setNote(""); reload(); }
    catch (er) { toast.error(errMsg(er)); }
  };

  return (
    <div>
      <PageHeader eyebrow="Disbursement gate" title="Payment holds" desc="Checks wage logs against verified challans before a vendor invoice is paid. Failing vendors are set to BLOCK DISBURSEMENT." />
      <div data-testid="hold-check-bar"><VendorMonthBar vendors={vendors} vendorId={vendorId} setVendorId={setVendorId} month={month} setMonth={setMonth}>
        <Button onClick={run} disabled={!vendorId} data-testid="run-hold-check-btn" className="bg-[#00E599] text-[#0B1017] hover:bg-[#00C482] font-semibold h-10"><Play size={14} className="mr-2" />Run check</Button>
      </VendorMonthBar></div>
      {result && <Result r={result} />}
      <div className="c-surface overflow-x-auto c-rise">
        <table className="c-table w-full">
          <thead><tr><th>Vendor</th><th>Month</th><th>Decision</th><th>Reason</th><th>Checked</th><th /></tr></thead>
          <tbody>
            {(holds || []).map((h) => (
              <tr key={h.id} data-testid="hold-table-row">
                <td className="font-medium">{h.vendor_name}</td>
                <td className="font-mono text-xs">{h.billing_month}</td>
                <td>{h.released ? <Pill tone="amber">{h.released_via ? "Released (renewal approved)" : "Released (override)"}</Pill> : h.hold_payment ? <Pill tone="red">On hold</Pill> : <Pill tone="green">Release</Pill>}</td>
                <td className="text-xs"><div className="font-mono text-slate-400">{h.reason_code}</div><div className="text-slate-500 max-w-sm truncate">{h.release_note || h.reason}</div></td>
                <td className="font-mono text-xs text-slate-500">{new Date(h.checked_at).toLocaleString("en-IN")}</td>
                <td className="text-right">{h.hold_payment && !h.released && (h.reason_code === "CLRA_LICENCE_EXPIRED"
                  ? <span className="text-[11px] text-amber-300" data-testid="payment-hold-clra-note">Released when the PE approves the renewed licence</span>
                  : <Button size="sm" variant="outline" onClick={() => setRelease(h)} data-testid="payment-hold-release-btn" className="border-[#1F2A3E] hover:bg-[#1A2234] hover:text-white">Release</Button>)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {holds?.length === 0 && <Empty text="No checks run yet." />}
      </div>
      <section className="mt-10"><HoldReport /></section>

      {release && (
        <Dialog open onOpenChange={() => setRelease(null)}>
          <DialogContent className="bg-[#121824] border-[#1F2A3E]" data-testid="release-dialog">
            <DialogHeader><DialogTitle className="font-display text-xl">Override hold for {release.vendor_name}</DialogTitle></DialogHeader>
            <form onSubmit={doRelease} className="grid gap-4">
              <p className="text-xs text-slate-400">This releases payment despite <span className="font-mono">{release.reason_code}</span>. The vendor stays at HIGH risk and the override is logged as an alert.</p>
              <textarea required minLength={3} className="c-input h-24 py-2" placeholder="Reason for override…" value={note} onChange={(e) => setNote(e.target.value)} data-testid="release-note-input" />
              <Button type="submit" data-testid="release-confirm-btn" className="bg-amber-500 text-black hover:bg-amber-400 font-semibold">Confirm release</Button>
            </form>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
